import React, { useState } from "react";
import {
  Card, Typography, Upload, Button, Table, Tag, Alert,
  Steps, Space, message, Spin, Divider,
} from "antd";
import { UploadOutlined, CheckCircleOutlined, EyeOutlined } from "@ant-design/icons";
import { importacionApi } from "../api/client";

const { Title, Text } = Typography;
const { Dragger } = Upload;

type Step = "upload" | "preview" | "confirm" | "done";

export default function ImportacionPage() {
  const [step, setStep] = useState<Step>("upload");
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<any>(null);
  const [resultado, setResultado] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const handlePreview = async (f: File) => {
    setFile(f);
    setLoading(true);
    try {
      const res = await importacionApi.preview(f);
      setPreview(res.data);
      setStep("preview");
    } catch { message.error("Error procesando el archivo"); }
    finally { setLoading(false); }
  };

  const handleConfirmar = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const res = await importacionApi.confirmar(file);
      setResultado(res.data);
      setStep("done");
    } catch { message.error("Error durante la importación"); }
    finally { setLoading(false); }
  };

  const reset = () => {
    setStep("upload");
    setFile(null);
    setPreview(null);
    setResultado(null);
  };

  const previewColumns = [
    { title: "Fila", dataIndex: "fila", width: 60 },
    { title: "Nombre", dataIndex: "nombre", ellipsis: true },
    { title: "Estado Planner", dataIndex: "estado_planner", width: 130 },
    { title: "Estado nuevo", dataIndex: "estado_nuevo", width: 130, render: (v: string) => <Tag color="blue">{v}</Tag> },
    { title: "Módulo", dataIndex: "modulo", width: 90 },
    {
      title: "Acción", dataIndex: "accion", width: 100,
      render: (v: string) => (
        <Tag color={v === "importar" ? "green" : "orange"}>{v}</Tag>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0, color: "#1a1f5e" }}>Importar desde Planner / Excel</Title>
        <Text type="secondary" style={{ fontSize: 13 }}>
          Carga el fichero exportado de Planner. Los estados se mapean automáticamente.
        </Text>
      </div>

      {/* Steps */}
      <Steps
        size="small"
        current={["upload", "preview", "confirm", "done"].indexOf(step)}
        style={{ marginBottom: 24 }}
        items={[
          { title: "Subir fichero" },
          { title: "Revisar preview" },
          { title: "Confirmar importación" },
          { title: "Completado" },
        ]}
      />

      {/* Step 1: Upload */}
      {step === "upload" && (
        <Card size="small">
          <Dragger
            accept=".xlsx,.xls,.csv"
            beforeUpload={(f) => { handlePreview(f); return false; }}
            showUploadList={false}
            style={{ padding: "20px 0" }}
          >
            <p className="ant-upload-drag-icon">
              <UploadOutlined style={{ fontSize: 40, color: "#1a1f5e" }} />
            </p>
            <p className="ant-upload-text">Arrastra el Excel de Planner aquí</p>
            <p className="ant-upload-hint">
              Formatos: .xlsx, .xls — El sistema detecta automáticamente las columnas
            </p>
          </Dragger>
          {loading && (
            <div style={{ textAlign: "center", padding: 16 }}>
              <Spin tip="Procesando archivo..." />
            </div>
          )}
          <Divider />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Columnas reconocidas automáticamente: ID, Title/Nombre, Bucket Name/Estado, Due Date, % Complete, Priority
          </Text>
        </Card>
      )}

      {/* Step 2: Preview */}
      {step === "preview" && preview && (
        <Card size="small">
          <Space style={{ marginBottom: 16, justifyContent: "space-between", width: "100%" }}>
            <Space>
              <EyeOutlined />
              <Text strong>Preview de importación — {preview.total_filas} filas detectadas</Text>
            </Space>
            <Space>
              <Button onClick={reset}>Cancelar</Button>
              <Button type="primary" icon={<CheckCircleOutlined />}
                onClick={handleConfirmar} loading={loading} style={{ background: "#1a1f5e" }}>
                Confirmar e importar
              </Button>
            </Space>
          </Space>

          {preview.errores?.length > 0 && (
            <Alert type="warning" message={`${preview.errores.length} errores detectados`}
              description={preview.errores.slice(0, 5).map((e: any) => `Fila ${e.fila}: ${e.error}`).join(" | ")}
              style={{ marginBottom: 16 }} />
          )}

          <div style={{ marginBottom: 12 }}>
            <Space>
              <Tag color="green">A importar: {preview.preview?.filter((p: any) => p.accion === "importar").length}</Tag>
              <Tag color="orange">A omitir: {preview.omitidos}</Tag>
            </Space>
          </div>

          <Table
            dataSource={preview.preview}
            columns={previewColumns}
            rowKey="fila"
            size="small"
            pagination={{ pageSize: 15, showTotal: (t) => `${t} filas` }}
          />
        </Card>
      )}

      {/* Step 3: Done */}
      {step === "done" && resultado && (
        <Card size="small" style={{ textAlign: "center" }}>
          <CheckCircleOutlined style={{ fontSize: 48, color: "#52c41a", marginBottom: 16 }} />
          <Title level={4}>Importación completada</Title>
          <Space size={24} style={{ margin: "16px 0" }}>
            <Statistic label="Importados" value={resultado.importados} color="#52c41a" />
            <Statistic label="Omitidos" value={resultado.omitidos} color="#faad14" />
            <Statistic label="Errores" value={resultado.errores?.length ?? 0} color="#ff4d4f" />
          </Space>
          <br />
          <Button type="primary" onClick={reset} style={{ background: "#1a1f5e" }}>
            Nueva importación
          </Button>
        </Card>
      )}
    </div>
  );
}

function Statistic({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div>
      <div style={{ fontSize: 32, fontWeight: 700, color }}>{value}</div>
      <Text style={{ fontSize: 12, color: "#888" }}>{label}</Text>
    </div>
  );
}
